import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api.js';

const REFRESH_MS = 15000;

function clock(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Operations Wallboard (FR-TRK-1/2) — a read-only, auto-refreshing, per-port
 * "single glance" display for a control-room TV: active movements, exception
 * alarms, fleet availability, and live tracked assets. Fullscreen toggle hides
 * the app chrome for a clean wall display.
 */
export default function OpsWallboardPage() {
  const [ports, setPorts] = useState([]);
  const [portName, setPortName] = useState('all');
  const [data, setData] = useState({ assets: [], exceptions: [], tickets: [], fleet: [], bookings: [] });
  const [updatedAt, setUpdatedAt] = useState(null);
  const [error, setError] = useState('');
  const rootRef = useRef(null);
  const [isFull, setIsFull] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [snapshot, exceptions, tickets, fleet, bookings] = await Promise.all([
        api.getTrackingSnapshot().catch(() => null),
        api.getExceptions().catch(() => []),
        api.listJobTickets().catch(() => []),
        api.getTugTimeline().catch(() => []),
        api.listConfirmedBookings().catch(() => []),
      ]);
      setData({
        assets: snapshot?.assets || snapshot || [],
        exceptions: exceptions || [],
        tickets: tickets || [],
        fleet: fleet || [],
        bookings: bookings || [],
      });
      setUpdatedAt(new Date());
      setError('');
    } catch (err) {
      setError(err.message || 'Wallboard refresh failed.');
    }
  }, []);

  useEffect(() => {
    api.listPorts().then(setPorts).catch(() => {});
    refresh();
    const id = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(id);
  }, [refresh]);

  useEffect(() => {
    const onFs = () => setIsFull(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const toggleFull = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else rootRef.current?.requestFullscreen?.();
  };

  // Port filter (where the datum carries a port).
  const matchPort = useCallback(
    (p) => portName === 'all' || (p && String(p).toLowerCase() === portName.toLowerCase()),
    [portName]
  );
  const bookings = useMemo(
    () => data.bookings.filter((b) => matchPort(b.port_name)),
    [data.bookings, matchPort]
  );
  const fleet = data.fleet;
  const exceptions = data.exceptions;
  const assets = data.assets;

  const avail = fleet.filter((r) => !(r.maintenanceWindows || []).length).length;
  const downtime = fleet.length - avail;
  const conflicts = fleet.filter((r) => r.hasConflict).length;
  const critical = exceptions.filter((e) => e.severity === 'Critical').length;

  const tile = { background: '#0f2a5e', color: '#e6edf9', borderRadius: 12 };
  const Stat = ({ value, label, tone }) => (
    <div className="text-center px-3">
      <div className="fw-bold" style={{ fontSize: '2.4rem', lineHeight: 1, color: tone }}>{value}</div>
      <div className="small text-uppercase" style={{ letterSpacing: '0.06em', opacity: 0.7 }}>{label}</div>
    </div>
  );

  return (
    <div ref={rootRef} style={{ background: '#081427', minHeight: isFull ? '100vh' : 'auto' }} className="p-3" data-testid="ops-wallboard-page">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 text-white">
        <h3 className="mb-0 fw-bold">
          <i className="fas fa-tower-broadcast me-2 text-info" aria-hidden="true" />
          Operations Wallboard
          <span className="ms-3 badge bg-info text-dark align-middle" data-testid="wb-port">
            {portName === 'all' ? 'All Ports' : portName}
          </span>
        </h3>
        <div className="d-flex align-items-center gap-3">
          <select
            className="form-select form-select-sm"
            style={{ width: 200 }}
            value={portName}
            onChange={(e) => setPortName(e.target.value)}
            data-testid="wb-port-select"
          >
            <option value="all">All Ports</option>
            {ports.map((p) => (
              <option key={p.id} value={p.name}>{p.name}</option>
            ))}
          </select>
          <span className="small text-white-50" data-testid="wb-updated">Updated {clock(updatedAt)}</span>
          <button className="btn btn-sm btn-outline-light" onClick={toggleFull} data-testid="wb-fullscreen">
            <i className={`fas fa-${isFull ? 'compress' : 'expand'} me-1`} aria-hidden="true" />
            {isFull ? 'Exit' : 'Fullscreen'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {/* Top stat strip */}
      <div className="d-flex justify-content-around align-items-center py-3 mb-3 shadow-sm" style={tile} data-testid="wb-stats">
        <Stat value={bookings.length} label="Active Movements" tone="#7cc4ff" />
        <Stat value={data.tickets.filter((t) => t.overdue).length} label="Overdue Tickets" tone="#ffb454" />
        <Stat value={critical} label="Critical Alarms" tone="#ff6b6b" />
        <Stat value={avail} label="Tugs Available" tone="#51cf66" />
        <Stat value={downtime} label="In Downtime" tone="#ffd43b" />
        <Stat value={assets.length} label="Tracked Assets" tone="#74c0fc" />
      </div>

      <div className="row g-3">
        {/* Active movements */}
        <div className="col-lg-4">
          <div className="p-3 h-100 shadow-sm" style={tile} data-testid="wb-movements">
            <h6 className="text-uppercase small fw-bold mb-3" style={{ opacity: 0.7 }}>
              <i className="fas fa-route me-2" aria-hidden="true" />Active Movements
            </h6>
            {bookings.length === 0 ? (
              <div className="small" style={{ opacity: 0.6 }}>No active movements.</div>
            ) : (
              bookings.slice(0, 8).map((b) => (
                <div key={b.id} className="d-flex justify-content-between border-bottom border-secondary py-2 small">
                  <span><strong>{b.booking_ref}</strong> · {b.vessel_name || 'Vessel'}</span>
                  <span style={{ opacity: 0.8 }}>{b.movement_type} · {b.port_name || '—'}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Exception alarms */}
        <div className="col-lg-4">
          <div className="p-3 h-100 shadow-sm" style={tile} data-testid="wb-alarms">
            <h6 className="text-uppercase small fw-bold mb-3" style={{ opacity: 0.7 }}>
              <i className="fas fa-triangle-exclamation me-2 text-warning" aria-hidden="true" />Exception Alarms
            </h6>
            {exceptions.length === 0 ? (
              <div className="small text-success">All clear.</div>
            ) : (
              exceptions.slice(0, 8).map((e) => (
                <div key={e.alertId} className="d-flex justify-content-between border-bottom border-secondary py-2 small">
                  <span>
                    <span className={`badge me-2 ${e.severity === 'Critical' ? 'bg-danger' : 'bg-warning text-dark'}`}>
                      {e.category}
                    </span>
                    {e.bookingRef || e.tugName || ''}
                  </span>
                  <span style={{ opacity: 0.75 }}>{e.vesselName || ''}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Fleet availability */}
        <div className="col-lg-4">
          <div className="p-3 h-100 shadow-sm" style={tile} data-testid="wb-fleet">
            <h6 className="text-uppercase small fw-bold mb-3" style={{ opacity: 0.7 }}>
              <i className="fas fa-ship me-2" aria-hidden="true" />Fleet Availability
              {conflicts > 0 && <span className="badge bg-danger ms-2">{conflicts} conflict</span>}
            </h6>
            {fleet.length === 0 ? (
              <div className="small" style={{ opacity: 0.6 }}>No fleet data.</div>
            ) : (
              fleet.slice(0, 8).map((r) => {
                const down = (r.maintenanceWindows || []).length > 0;
                return (
                  <div key={r.tugId} className="d-flex justify-content-between border-bottom border-secondary py-2 small">
                    <span>{r.name}</span>
                    <span className={`badge ${r.hasConflict ? 'bg-danger' : down ? 'bg-warning text-dark' : 'bg-success'}`}>
                      {r.hasConflict ? 'Conflict' : down ? 'Downtime' : 'Available'}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Live tracked assets */}
        <div className="col-12">
          <div className="p-3 shadow-sm" style={tile} data-testid="wb-assets">
            <h6 className="text-uppercase small fw-bold mb-3" style={{ opacity: 0.7 }}>
              <i className="fas fa-satellite-dish me-2 text-info" aria-hidden="true" />Live Tracked Assets (AIS / GPS)
            </h6>
            <div className="d-flex flex-wrap gap-2">
              {assets.length === 0 ? (
                <div className="small" style={{ opacity: 0.6 }}>No live positions.</div>
              ) : (
                assets.map((a) => (
                  <span key={a.assetId || a.asset_id || a.name} className="badge bg-dark border border-secondary p-2">
                    <i className={`fas fa-${(a.assetType || a.asset_type) === 'tug' ? 'ship' : 'water'} me-1 text-info`} aria-hidden="true" />
                    {a.name} · {Math.round(a.speedKnots ?? a.speed_knots ?? 0)} kn · {a.status || '—'}
                  </span>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
